import { axiosInstance } from './api';

/**
 * Curiosity API Service
 *
 * Handles all API calls for the Story Curiosity system:
 * - Questions (asking, listing, responding)
 * - Expansions (viewing, reacting)
 */

// ============ Questions ============

/**
 * Get all questions for a specific story
 */
export const getStoryQuestions = async (storyId) => {
  const response = await axiosInstance.get(`/curiosity/stories/${storyId}/questions/`);
  return response.data;
};

/**
 * Ask a question about a story
 */
export const askQuestion = async (storyId, questionData) => {
  const response = await axiosInstance.post(`/curiosity/stories/${storyId}/questions/`, questionData);
  return response.data;
};

/**
 * Get questions waiting for the current user to answer
 */
export const getWaitingQuestions = async () => {
  const response = await axiosInstance.get('/curiosity/questions/waiting/');
  return response.data;
};

/**
 * Get a single question by ID
 */
export const getQuestion = async (questionId) => {
  const response = await axiosInstance.get(`/curiosity/questions/${questionId}/`);
  return response.data;
};

/**
 * Update a question (only the asker can edit their own questions)
 * @param {string} questionId - The question UUID
 * @param {object} questionData - Data to update (e.g., { question_text: 'new text' })
 */
export const updateQuestion = async (questionId, questionData) => {
  const response = await axiosInstance.patch(`/curiosity/questions/${questionId}/`, questionData);
  return response.data;
};

/**
 * Respond to a question (create an expansion)
 */
export const respondToQuestion = async (questionId, responseData) => {
  const response = await axiosInstance.post(`/curiosity/questions/${questionId}/respond/`, responseData);
  return response.data;
};

/**
 * Decline to answer a question
 */
export const declineQuestion = async (questionId) => {
  const response = await axiosInstance.post(`/curiosity/questions/${questionId}/decline/`);
  return response.data;
};

// ============ Expansions ============

/**
 * Get all expansions for a specific story
 */
export const getStoryExpansions = async (storyId) => {
  const response = await axiosInstance.get(`/curiosity/stories/${storyId}/expansions/`);
  return response.data;
};

/**
 * Get a single expansion by ID
 */
export const getExpansion = async (expansionId) => {
  const response = await axiosInstance.get(`/curiosity/expansions/${expansionId}/`);
  return response.data;
};

/**
 * React to an expansion
 * @param {string} expansionId - The expansion UUID
 * @param {string} reactionType - One of: 'heart', 'thank_you', 'learned', 'treasure'
 */
export const reactToExpansion = async (expansionId, reactionType) => {
  const response = await axiosInstance.post(`/curiosity/expansions/${expansionId}/react/`, {
    reaction_type: reactionType
  });
  return response.data;
};

// ============ Question Types ============

/**
 * Available question types with labels and icons
 */
export const QUESTION_TYPES = [
  { value: 'who_is_this', label: 'Who is this?', icon: '👤' },
  { value: 'tell_me_more', label: 'Tell me more', icon: '📖' },
  { value: 'what_happened', label: 'What happened next?', icon: '➡️' },
  { value: 'when_was_this', label: 'When was this?', icon: '📅' },
  { value: 'where_was_this', label: 'Where was this?', icon: '📍' },
  { value: 'how_did_you_feel', label: 'How did you feel?', icon: '💭' },
  { value: 'general', label: 'Other question', icon: '❓' },
];

/**
 * Reaction types with labels and emojis
 */
export const REACTION_TYPES = [
  { value: 'heart', label: 'Love', emoji: '❤️' },
  { value: 'thank_you', label: 'Thank you', emoji: '🙏' },
  { value: 'learned', label: 'Learned something', emoji: '💡' },
  { value: 'treasure', label: 'Treasure', emoji: '💎' },
];

export default {
  getStoryQuestions,
  askQuestion,
  getWaitingQuestions,
  getQuestion,
  updateQuestion,
  respondToQuestion,
  declineQuestion,
  getStoryExpansions,
  getExpansion,
  reactToExpansion,
  QUESTION_TYPES,
  REACTION_TYPES,
};
